import apiClient from '@/lib/api-client';
import type {
  Subscription,
  CreateSubscriptionRequest,
  ChangeSubscriptionRequest,
  SubscriptionResponse,
  SubscriptionLimits,
  ApiPlan,
  ApiPlansResponse,
  Currency,
  PromoCodeValidationResponse,
  SubscriptionPlan,
} from '@/types/subscription';

class SubscriptionService {
  /**
   * Create a new subscription for the organization
   */
  async createSubscription(data: CreateSubscriptionRequest): Promise<SubscriptionResponse> {
    const { data: response } = await apiClient.post<{ data: any }>(
      '/v1/subscriptions',
      {
        subscription: {
          plan_id: data.planId,
          billing_cycle: data.billingCycle,
          payment_method_id: data.paymentMethodId,
          promotion_code: data.promotionCode,
        },
      }
    );

    return this.transformSubscriptionResponse(response.data);
  }

  /**
   * Validate a promo code against Stripe
   * Returns discount details if valid
   */
  async validatePromoCode(code: string): Promise<PromoCodeValidationResponse> {
    const { data: response } = await apiClient.post<{ data: any }>(
      '/v1/promo_codes/validate',
      { code }
    );

    return {
      valid: response.data.valid,
      promotionCodeId: response.data.promotion_code_id,
      code: response.data.code,
      discount: {
        type: response.data.discount.type,
        percentOff: response.data.discount.percent_off,
        amountOff: response.data.discount.amount_off,
        currency: response.data.discount.currency,
        duration: response.data.discount.duration,
        durationInMonths: response.data.discount.duration_in_months,
      },
      restrictions: {
        firstTimeTransaction: response.data.restrictions.first_time_transaction,
        minimumAmount: response.data.restrictions.minimum_amount,
        minimumAmountCurrency: response.data.restrictions.minimum_amount_currency,
      },
    };
  }

  /**
   * Get list of subscriptions for the organization
   */
  async listSubscriptions(): Promise<Subscription[]> {
    const { data: response } = await apiClient.get<{ data: any[] }>('/v1/subscriptions');

    return response.data.map((sub) => this.transformSubscription(sub));
  }

  /**
   * Get current active subscription
   */
  async getCurrentSubscription(): Promise<SubscriptionResponse | null> {
    try {
      const { data: response } = await apiClient.get<{ data: any }>(
        '/v1/subscriptions/current'
      );

      if (!response.data) {
        return null;
      }

      // The API returns { subscription: {...all fields including next_billing_date/amount...} }
      // We need to extract the subscription object and pull out the billing fields
      const subscriptionData = response.data.subscription;
      
      if (!subscriptionData) {
        return null;
      }

      const transformed: SubscriptionResponse = {
        subscription: this.transformSubscription(subscriptionData),
        nextBillingDate: subscriptionData.next_billing_date || null,
        nextBillingAmount: subscriptionData.next_billing_amount !== undefined ? subscriptionData.next_billing_amount : null,
        nextBillingCurrency: subscriptionData.next_billing_currency || subscriptionData.currency || 'USD',
      };
      
      return transformed;
    } catch (error: any) {
      // If 404, no active subscription exists
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Change subscription plan
   */
  async changeSubscriptionPlan(
    id: number,
    data: ChangeSubscriptionRequest
  ): Promise<SubscriptionResponse> {
    const { data: response } = await apiClient.put<{ data: any }>(
      `/v1/subscriptions/${id}`,
      {
        subscription: {
          new_plan_id: data.newPlanId,
          billing_cycle: data.billingCycle,
        },
      }
    );

    return this.transformSubscriptionResponse(response.data);
  }

  /**
   * Cancel a subscription
   */
  async cancelSubscription(cancelImmediately = false): Promise<any> {
    const { data: response } = await apiClient.post<{ data: any }>(
      `/v1/subscriptions/cancel`,
      {
        cancel_immediately: cancelImmediately,
      }
    );

    return response.data;
  }

  /**
   * Resume a canceled subscription (before end of current period)
   */
  async resumeSubscription(): Promise<any> {
    const { data: response } = await apiClient.post<{ data: any }>(
      `/v1/subscriptions/resume`
    );

    return response.data;
  }

  /**
   * Get subscription limits and feature availability
   */
  async getSubscriptionLimits(): Promise<SubscriptionLimits> {
    const { data: response } = await apiClient.get<{ data: any }>(
      '/v1/admin/subscriptions/limits'
    );

    return this.transformSubscriptionLimits(response.data);
  }

  /**
   * Get available subscription plans (public endpoint)
   * @param currency Optional currency override (usd, eur, brl)
   */
  async getPlans(currency?: Currency): Promise<ApiPlansResponse> {
    const params = currency ? { currency } : {};
    const { data: response } = await apiClient.get<ApiPlansResponse>(
      '/v1/subscription_plans',
      { params }
    );

    return response;
  }

  /**
   * Get Stripe Customer Portal URL
   * Opens Stripe's hosted portal for managing subscriptions, payment methods, and invoices
   * @param locale Current app language (en, pt, de)
   */
  async getBillingPortalUrl(locale?: string): Promise<string> {
    const { data: response } = await apiClient.get<{ data: { url: string } }>(
      '/v1/subscriptions/billing_portal',
      { params: { locale } }
    );

    return response.data.url;
  }

  /**
   * Get Stripe Customer Portal URL for plan change
   * Opens directly to the subscription update page with proration preview
   * @param locale Current app language (en, pt, de)
   */
  async getPlanChangePortalUrl(locale?: string): Promise<string> {
    const { data: response } = await apiClient.get<{ data: { url: string } }>(
      '/v1/subscriptions/billing_portal',
      { params: { locale, flow: 'subscription_update' } }
    );

    return response.data.url;
  }

  /**
   * Get Stripe Customer Session for pricing table
   * Creates a customer session that allows the pricing table to show
   * the user's current plan and "Switch plan" buttons instead of "Subscribe"
   */
  async getCustomerSession(): Promise<{ clientSecret: string } | null> {
    try {
      const { data: response } = await apiClient.get<{ data: { client_secret: string } }>(
        '/v1/subscriptions/customer_session'
      );

      return { clientSecret: response.data.client_secret };
    } catch (error: any) {
      // If 404 or 422, user doesn't have a Stripe customer yet
      if (error.response?.status === 404 || error.response?.status === 422) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Get all available subscription plans from Stripe
   */
  async getStripePlans(): Promise<SubscriptionPlan[]> {
    const { data: response } = await apiClient.get<{ plans: any[] }>(
      '/v1/subscription_plans/stripe_plans'
    );

    return response.plans.map((plan: any) => ({
      id: plan.id,
      name: plan.name,
      description: plan.description,
      metadata: plan.metadata,
      marketingFeatures: plan.marketing_features || [],
      prices: plan.prices.map((price: any) => ({
        id: price.id,
        currency: price.currency,
        unitAmount: price.unit_amount,
        recurring: price.recurring ? {
          interval: price.recurring.interval,
          intervalCount: price.recurring.interval_count,
        } : null,
        type: price.type,
      })),
    }));
  }

  /**
   * Get portal URL to switch to a specific plan
   * @param priceId Stripe price ID to switch to
   * @param locale Current app language (en, pt, de)
   */
  async getSwitchPlanUrl(priceId: string, locale?: string): Promise<string> {
    const { data: response } = await apiClient.get<{ data: { url: string } }>(
      `/v1/subscriptions/${priceId}/switch_plan`,
      { params: { locale } }
    );

    return response.data.url;
  }

  /**
   * Create a Stripe Checkout Session for a new subscription
   * @param priceId Stripe price ID to subscribe to
   * @param locale Current app language (en, pt, de)
   * @param currency Billing currency for the multi-currency price (usd, eur, brl, pln)
   */
  async createCheckoutSession(priceId: string, locale?: string, currency?: string): Promise<string> {
    const { data: response } = await apiClient.post<{ data: { url: string } }>(
      '/v1/subscriptions/checkout',
      { price_id: priceId, currency },
      { params: { locale } }
    );

    return response.data.url;
  }

  // Transformation methods
  private transformSubscription(apiSub: any): Subscription {
    return {
      id: apiSub.id,
      organizationId: apiSub.organization_id,
      planId: apiSub.plan_id,
      planCode: apiSub.plan_code,
      planName: apiSub.plan_name,
      status: apiSub.status,
      billingCycle: apiSub.billing_cycle,
      currency: apiSub.currency,
      currentPeriodStart: apiSub.current_period_start,
      currentPeriodEnd: apiSub.current_period_end,
      trialStart: apiSub.trial_start,
      trialEnd: apiSub.trial_end,
      cancelAt: apiSub.cancel_at,
      canceledAt: apiSub.canceled_at,
      createdAt: apiSub.created_at,
      updatedAt: apiSub.updated_at,
    };
  }

  private transformSubscriptionResponse(apiResponse: any): SubscriptionResponse {
    return {
      subscription: this.transformSubscription(apiResponse.subscription),
      nextBillingDate: apiResponse.next_billing_date,
      nextBillingAmount: apiResponse.next_billing_amount,
      nextBillingCurrency: apiResponse.next_billing_currency,
    };
  }

  private transformSubscriptionLimits(apiData: any): SubscriptionLimits {
    // Helper to determine if a limit is "unlimited" (enterprise plan uses 9999)
    const isUnlimited = (limit: number) => limit >= 9999;

    return {
      currentPlan: {
        code: apiData.current_plan.code,
        name: apiData.current_plan.name,
        displayName: apiData.current_plan.display_name,
        description: apiData.current_plan.description,
      },
      usage: {
        users: {
          current: apiData.usage.max_users.current,
          limit: apiData.usage.max_users.limit,
          percentage: apiData.usage.max_users.percentage,
          unlimited: isUnlimited(apiData.usage.max_users.limit),
        },
        providers: {
          current: apiData.usage.max_providers.current,
          limit: apiData.usage.max_providers.limit,
          percentage: apiData.usage.max_providers.percentage,
          unlimited: isUnlimited(apiData.usage.max_providers.limit),
        },
        services: {
          current: apiData.usage.max_services.current,
          limit: apiData.usage.max_services.limit,
          percentage: apiData.usage.max_services.percentage,
          unlimited: isUnlimited(apiData.usage.max_services.limit),
        },
        bookingsThisMonth: {
          current: apiData.usage.max_bookings_per_month.current,
          limit: apiData.usage.max_bookings_per_month.limit,
          percentage: apiData.usage.max_bookings_per_month.percentage,
          unlimited: isUnlimited(apiData.usage.max_bookings_per_month.limit),
        },
      },
      features: {
        emailNotifications: apiData.features.email_notifications,
        smsNotifications: apiData.features.sms_notifications,
        dataExport: apiData.features.data_export,
        calendarIntegration: apiData.features.calendar_integration,
        apiAccess: apiData.features.api_access,
        webhooks: apiData.features.webhooks,
        customIntegrations: apiData.features.custom_integrations,
        teamCollaboration: apiData.features.team_collaboration,
        advancedAnalytics: apiData.features.advanced_analytics,
        customDomain: apiData.features.custom_domain,
        whiteLabel: apiData.features.white_label,
        prioritySupport: apiData.features.priority_support,
        sla: apiData.features.sla,
      },
      metadata: apiData.metadata,
    };
  }
}

export const subscriptionService = new SubscriptionService();
